import * as React from "react";

import { faCalendarMinus, faCalendarPlus, faInfoCircle, faPen } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import useMedia from "~/lib/hooks/useMedia";
import { Menu, MenuButton, MenuItem, MenuItems, MenuPopover, positionRight } from "~/ui/dropdown";
import { useTourContext } from "~/welcomeChecklist";

import type {
	StatusChoices as CalendarEventStatus,
	ResponseStatusChoices,
} from "~/__generated__/MeetingsPanelQuery.graphql";

import agendaStyles from "./MeetingsPanel.global.module.css";

interface MeetingInfoButtonEventData {
	isOrganizer: boolean | null;
	status: CalendarEventStatus;
	attendeePersonCount: number | null;
	isHidden: boolean | null;
	userResponseStatus: ResponseStatusChoices | null;
}

interface MeetingInfoButtonProps {
	onOpenMeetingInfoModal: () => void;
	onEdit: () => void;
	onHide: () => void;
	handleAnswerEventAttendance: (response: ResponseStatusChoices) => () => void;
	event: MeetingInfoButtonEventData;
}

export default function MeetingInfoButton(props: MeetingInfoButtonProps) {
	const { activeTour } = useTourContext();
	const isTourActive = activeTour !== null;

	const { event, onEdit, onHide, onOpenMeetingInfoModal, handleAnswerEventAttendance } = props;
	const hasMultipleAttendees = event.attendeePersonCount ? event.attendeePersonCount > 0 : false;
	const canEdit = event.isOrganizer && event.status !== "cancelled";

	const isTouch = useMedia("(hover: none)");

	return isTouch ? (
		<button className="rbc-agenda-info-button" data-test-id="meeting-info-button" onClick={onOpenMeetingInfoModal}>
			<FontAwesomeIcon icon={faInfoCircle} />
		</button>
	) : (
		<Menu>
			{({ isOpen }) => (
				<>
					<MenuButton
						className={cx("rbc-agenda-info-button", { "agenda-info-button-visible": isOpen })}
						data-test-id="meeting-actions-button"
						disabled={isTourActive}
					>
						<FontAwesomeIcon icon={faEllipsisV} />
					</MenuButton>
					<MenuPopover position={positionRight}>
						<MenuItems className={agendaStyles.eventActionsPopup}>
							<MenuItem className={agendaStyles.eventAction} onSelect={onEdit} disabled={!canEdit}>
								<FontAwesomeIcon fixedWidth icon={faPen} />
								Edit event
							</MenuItem>
							<MenuItem
								className={agendaStyles.eventAction}
								data-test-id="meeting-info-button"
								onSelect={onOpenMeetingInfoModal}
							>
								<FontAwesomeIcon fixedWidth icon={faInfoCircle} />
								Event details
							</MenuItem>
							<MenuItem className={agendaStyles.eventAction} onSelect={onHide}>
								<FontAwesomeIcon fixedWidth icon={event.isHidden ? faCalendarPlus : faCalendarMinus} />
								{event.isHidden ? "Show" : "Hide"} event in Fellow
							</MenuItem>
							{event.status !== "cancelled" && !event.isOrganizer && hasMultipleAttendees && (
								<>
									<div className={agendaStyles.eventActionDivider} />
									<div className={agendaStyles.eventActionAttendanceWrapper}>
										<div>Going?</div>

										<div className={agendaStyles.attendanceButtons}>
											<MenuItem
												className={cx(agendaStyles.attendanceButton, {
													[agendaStyles.accepted]: event.userResponseStatus === "accepted",
												})}
												onSelect={handleAnswerEventAttendance("accepted")}
											>
												Yes
											</MenuItem>
											<MenuItem
												className={cx(agendaStyles.attendanceButton, {
													[agendaStyles.accepted]: event.userResponseStatus === "declined",
												})}
												onSelect={handleAnswerEventAttendance("declined")}
											>
												No
											</MenuItem>
											<MenuItem
												className={cx(agendaStyles.attendanceButton, {
													[agendaStyles.accepted]:
														event.userResponseStatus === "tentative" ||
														event.userResponseStatus === "tentativelyAccepted",
												})}
												onSelect={handleAnswerEventAttendance("tentative")}
											>
												Maybe
											</MenuItem>
										</div>
									</div>
								</>
							)}
						</MenuItems>
					</MenuPopover>
				</>
			)}
		</Menu>
	);
}
